import {
  useEffect,
  useRef,
  type FC,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ThemedLayout } from '~/layout/ThemedLayout';

export interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onOutsideClick?: () => void;
  children: ReactNode;
  className?: string;
  zIndex?: number;
}

export const Modal: FC<ModalProps> = ({
  isOpen,
  onClose,
  onOutsideClick,
  children,
  className = '',
  zIndex = 1000,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      if (onOutsideClick) {
        onOutsideClick();
      } else if (onClose) {
        onClose();
      }
    }
  };

  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;

      if (firstElement) {
        firstElement.focus();
      }
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const ModalContent = (
    <ThemedLayout>
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{
          zIndex,
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
        onClick={handleBackdropClick}
        role="Modal"
        aria-modal="true"
        aria-labelledby="Modal-title"
      >
        <div
          ref={modalRef}
          className={`
          relative
          shadow-2xl
          max-w-full
          max-h-full
          overflow-auto
          transform
          transition-all
          duration-500
          ease-out
          animate-in
          fade-in-0
          zoom-in-95
          ${className}
        `}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </div>
    </ThemedLayout>
  );

  return createPortal(ModalContent, document.body);
};
